import React, { useContext } from 'react';
import { translateBlock } from 'i18n';
import {
    BudgetProposalT,
    BudgetProposalSettingsT,
    CadrageT,
} from 'routes/BudgetModule/config/types';
import { AutocompletAble } from 'ui/DropdownItems/types';
import Flex from 'ui/Flex/Flex';
import GenericAutocomplete from 'ui/DropdownItems/GenericAutocomplete';
import { capitalize } from 'lodash';
import {
    getCustomFilterLabel,
    isCustomBudgetProposal,
} from 'routes/BudgetModule/components/BudgetProposal/Resource/hooks/budgetProposal';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import CadrageChart from './CadrageChart';
import BudgetProposalChart from './BudgetProposalChart';
import InscriptionStatusChart from './InscriptionStatusChart';
import { LINE_CODE_FIELD } from 'routes/BudgetModule/config/constants';
import { BudgetExpenseOrIncome, BudgetPhases } from 'business/constants';
import { isActiveBudgetPhase } from 'routes/BudgetModule/shared/phases/helpers';
import BudgetReportToggle from 'routes/BudgetModule/components/shared/BudgetReportToggle/BudgetReportToggle';
import { useGetBudgetUserExerciceSettings } from 'routes/BudgetModule/components/shared/BudgetReportToggle/hooks';
import { StyLayout, StyVerticalLayout } from './styles';
import { ExpenseIncomeImputationDataT } from '../../hooks/expenseIncomeImputations';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { filterRejectedInscriptionOrImputation } from 'routes/BudgetModule/components/BudgetOverview/AllOperations/helpers/dataHelpers';

type Props = {
    budgetProposal: BudgetProposalT;
    inscriptionsData: InscriptionT[];
    imputationsData: ExpenseIncomeImputationDataT[];
    settings: BudgetProposalSettingsT | undefined;
    onSettingsEdit: (arg1: Partial<BudgetProposalSettingsT>) => void;
    cadrageData: CadrageT[];
    code: AutocompletAble<string> | null;
    setCodes: React.Dispatch<
        React.SetStateAction<AutocompletAble<string> | null>
    >;
    codeOptions: AutocompletAble<string>[];
    expenseOrIncome: BudgetExpenseOrIncome;
    hasOrphanInscriptions: boolean;
    resourceSoftwareId: string;
};

const ArbitrageChartSection = ({
    budgetProposal,
    inscriptionsData,
    imputationsData,
    cadrageData,
    settings,
    onSettingsEdit,
    code,
    setCodes,
    codeOptions,
    expenseOrIncome,
    hasOrphanInscriptions,
    resourceSoftwareId,
}: Props): React.ReactElement => {
    const {
        budgetReportExerciceData: { isReportedAmountUsed },
    } = useGetBudgetUserExerciceSettings(budgetProposal.exercice);

    const statusesSettings = useContext(StatusSettingsContext);

    // ---- SCHOOL SPECIFIC CODE
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);

    const filteredImputationData = isSchool
        ? imputationsData.filter((e) => e[LINE_CODE_FIELD] === code?.value)
        : imputationsData;
    const filteredInscriptionData = isSchool
        ? inscriptionsData.filter((e) => e[LINE_CODE_FIELD] === code?.value)
        : inscriptionsData;

    const getDataByPhase = (
        budgetPhase: BudgetPhases,
    ): InscriptionT[] | ExpenseIncomeImputationDataT[] => {
        if (isActiveBudgetPhase(budgetPhase)) return filteredInscriptionData;
        return filteredImputationData;
    };

    const { phase, budgetName, section } = budgetProposal;
    const dataByPhase = getDataByPhase(phase) as (
        | InscriptionT
        | ExpenseIncomeImputationDataT
    )[];
    const filteredDataByPhase =
        filterRejectedInscriptionOrImputation(dataByPhase);

    return (
        <StyVerticalLayout>
            <Flex direction="row" justifyContent="end">
                {isSchool ? (
                    <GenericAutocomplete
                        selectedValues={code}
                        availableValues={codeOptions}
                        setSelectedValues={setCodes}
                        label={capitalize(
                            translateBlock(
                                getCustomFilterLabel(integrations),
                                true,
                            ),
                        )}
                        sx={{
                            m: '0',
                            width: '20%',
                            mb: '1rem',
                            pt: '5px',
                            pr: '5px',
                        }}
                    />
                ) : (
                    <BudgetReportToggle
                        exercice={budgetProposal.exercice}
                        context={{
                            page: 'Dépenses/Recettes',
                            budgetProposal: budgetName,
                            section: section ?? '',
                        }}
                    />
                )}
            </Flex>
            <StyLayout>
                <CadrageChart
                    budgetProposal={budgetProposal}
                    cadrageData={cadrageData}
                    filteredImputationData={filteredImputationData}
                    dataByPhase={filteredDataByPhase}
                    isReportedAmountUsed={isReportedAmountUsed}
                    reverseColor={
                        expenseOrIncome === BudgetExpenseOrIncome.EXPENSE
                    }
                    hasOrphanInscriptions={hasOrphanInscriptions}
                    resourceSoftwareId={resourceSoftwareId}
                />
                <BudgetProposalChart
                    budgetProposal={budgetProposal}
                    settings={settings}
                    onSettingsEdit={onSettingsEdit}
                    statusesSettings={statusesSettings}
                    filteredImputationData={filteredImputationData}
                    dataByPhase={filteredDataByPhase}
                    isReportedAmountUsed={isReportedAmountUsed}
                />
                <InscriptionStatusChart
                    filteredInscriptionData={filteredInscriptionData}
                />
            </StyLayout>
        </StyVerticalLayout>
    );
};

export default ArbitrageChartSection;
